import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Dialog, Icon, Textarea, Typography, Popover, Progress } from '@equinor/eds-core-react'
import { close, help_outline } from '@equinor/eds-icons'
import { baseFont, VContainer } from '@/styles/layout'
import { useConfirmDialog, useInfoDialog } from '@/hooks'
import { insertImage } from '@/api/apiService'
import { addSnackBarMessage } from '@/core/store/globalStore'
import { useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from '@/core/models/enum'
import { dataUrlToBlob } from '../utils'

interface ImageProps {
    plant: string
    tag: string
    showImage: boolean
    onClose: () => void
}

const tooltipImage = 'Describe the purpose of this image. Max 240 characters.'

const TakePhoto: React.FC<ImageProps> = ({ plant, tag, showImage, onClose }) => {
    const [photo, setPhoto] = useState<string | null>(null)
    const [description, setDescription] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [stream, setStream] = useState<MediaStream | null>(null)
    const [isUploading, setIsUploading] = useState(false)
    const [imageEdited, setImageEdited] = useState(false)
    const [isTooltipDescriptionOpen, setTooltipDescriptionIsOpen] = useState(false)
    const descriptionRef = useRef<HTMLDivElement>(null)

    const videoRef = useRef<HTMLVideoElement>(null)
    const canvasRef = useRef<HTMLCanvasElement>(null)

    const queryClient = useQueryClient()

    const { showConfirm } = useConfirmDialog({
        title: 'Unsaved changes',
        content: 'You have unsaved changes. Are you sure you want to proceed?',
        rejectButtonTitle: 'Discard changes',
        confirmButtonTitle: 'Stay on page',
        onConfirm: () => {},
        onReject: () => onClose(),
        autoClose: true,
    })

    const { showInfo } = useInfoDialog(
        {
            title: 'Access Required',
            content: (
                <VContainer $gap="16px">
                    <Typography>
                        You do not have permission to upload image. Please request the role “STID Read Internal” for
                        relevant plant in AccessIT to get access.
                    </Typography>
                </VContainer>
            ),
            type: 'Info',
        },
        []
    )

    useEffect(() => {
        if (!showImage) return

        let cancelled = false

        const start = async () => {
            setError(null)
            setPhoto(null)
            setDescription('')
            setIsUploading(false)

            try {
                const s = await navigator.mediaDevices.getUserMedia({
                    video: { facingMode: { ideal: 'environment' } },
                    audio: false,
                })
                if (cancelled) return

                setStream(s)
                if (videoRef.current) {
                    videoRef.current.srcObject = s
                    await videoRef.current.play()
                }
            } catch (e: any) {
                setError(
                    e?.name === 'NotAllowedError'
                        ? 'Camera permission was denied.'
                        : e?.name === 'NotFoundError'
                          ? 'No camera device found.'
                          : 'Could not open camera.'
                )
            }
        }

        start()

        return () => {
            cancelled = true
            setStream((prev) => {
                prev?.getTracks().forEach((t) => t.stop())
                return null
            })
        }
    }, [showImage])

    const handleChangeDescription = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { value } = e.target
        setImageEdited(true)
        setDescription(value)
    }

    const stopStream = () => {
        stream?.getTracks().forEach((t) => t.stop())
        setStream(null)
    }

    const handleClose = () => {
        if (imageEdited) {
            showConfirm()
        } else {
            stopStream()
            onClose()
        }
    }

    const takePhoto = () => {
        const video = videoRef.current
        const canvas = canvasRef.current
        if (!video || !canvas) return

        const width = video.videoWidth
        const height = video.videoHeight
        if (!width || !height) return

        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        ctx.drawImage(video, 0, 0, width, height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9)

        setPhoto(dataUrl)
        stopStream()
    }

    const retake = async () => {
        setPhoto(null)
        setDescription('')
        setError(null)

        try {
            const s = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: { ideal: 'environment' } },
                audio: false,
            })
            setStream(s)
            if (videoRef.current) {
                videoRef.current.srcObject = s
                await videoRef.current.play()
            }
        } catch (e: any) {
            setError(
                e?.name === 'NotAllowedError'
                    ? 'Camera permission was denied.'
                    : e?.name === 'NotFoundError'
                      ? 'No camera device found.'
                      : 'Could not open camera.'
            )
        }
    }

    const upload = async () => {
        if (!photo) return

        setIsUploading(true)
        setError(null)

        try {
            const blob = dataUrlToBlob(photo)
            const file = new File([blob], 'photo.jpg', { type: blob.type })
            const response = await insertImage(plant, tag, file, description)
            if (response) {
                setImageEdited(false)
                addSnackBarMessage(`Image added`)
                queryClient.invalidateQueries({
                    queryKey: [QueryKeys.TagDetails],
                })
            } else {
                const message = `Failed to insert image for ${tag}.`
                showInfo()
                addSnackBarMessage(message)
                return
            }
            onClose()
        } catch (e: any) {
            setError(e?.message ?? `Failed to insert image for ` + tag)
        } finally {
            setIsUploading(false)
        }
    }

    return (
        <ImageContainer color="black" open={showImage} onClose={handleClose} isDismissable>
            <Header>
                <HeaderLabel>Take photo</HeaderLabel>
                <CloseButton as="Link" onClick={handleClose}>
                    <Icon data={close} size={24} style={{ color: '#007079' }} />
                </CloseButton>
            </Header>

            {error && <ErrorText>{error}</ErrorText>}

            {!error && !photo && <VideoView ref={videoRef} playsInline muted />}

            {photo && (
                <>
                    <ImageView src={photo} alt="Captured photo" />

                    <StyledTextArea
                        label={
                            <RowStyle>
                                <InputStyledLabel>Image description</InputStyledLabel>
                                <div ref={descriptionRef}>
                                    <Button
                                        variant="ghost_icon"
                                        aria-label="save action"
                                        onClick={() => setTooltipDescriptionIsOpen(!isTooltipDescriptionOpen)}
                                    >
                                        <Icon data={help_outline} size={24}></Icon>
                                    </Button>
                                </div>
                            </RowStyle>
                        }
                        meta={
                            <RowStyle>
                                <InputStyledLabel>Mandatory</InputStyledLabel>
                            </RowStyle>
                        }
                        id="UserAddedDescription"
                        value={description ? description : ''}
                        onChange={handleChangeDescription}
                        required
                        rows={3}
                        maxLength={240}
                        placeholder={'Add description'}
                        disabled={isUploading}
                    />
                    <RowContainerBottom>
                        <Button as="button" onClick={retake} variant="outlined" disabled={isUploading}>
                            <Label>Retake</Label>
                        </Button>
                        <Button as="button" onClick={upload} disabled={isUploading || !photo || !description.trim()}>
                            {isUploading ? <Progress.Dots /> : <StyledActionLabel>Upload</StyledActionLabel>}
                        </Button>
                    </RowContainerBottom>
                </>
            )}

            {!error && !photo && (
                <RowContainerBottom>
                    <Button onClick={takePhoto} disabled={!stream}>
                        Take photo
                    </Button>
                </RowContainerBottom>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />

            <Popover
                open={isTooltipDescriptionOpen}
                anchorEl={descriptionRef.current}
                onClose={() => setTooltipDescriptionIsOpen(false)}
                style={{ width: '300px' }}
            >
                <Popover.Header>
                    <Popover.Title>Image description</Popover.Title>
                    <Button
                        style={{
                            height: '32px',
                            width: '32px',
                        }}
                        variant="ghost_icon"
                        aria-label="Close popover"
                        onClick={() => setTooltipDescriptionIsOpen(false)}
                    >
                        <Icon data={close} size={24} />
                    </Button>
                </Popover.Header>
                <Popover.Content>
                    <StyledPopoverContent variant="body_short">{tooltipImage}</StyledPopoverContent>
                </Popover.Content>
            </Popover>
        </ImageContainer>
    )
}

const StyledActionLabel = styled(Typography)`
    ${baseFont}
    color: white;
    &:hover {
        color: white;
        background: #007079;
    }
`

const VideoView = styled.video`
    max-height: 370px;
`

const ImageContainer = styled(Dialog)`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 550px;
    max-width: calc(100% - 32px);
    padding: 16px;
    max-height: 80vh;

    @media (max-width: 1024px) {
        overflow: hidden;
    }
`

const ImageView = styled.img`
    max-height: 40vh;
`

const ErrorText = styled(Typography)`
    color: #b30d2f;
    margin: 8px 0 12px;
`

const HeaderLabel = styled(Typography)`
    font-family: Equinor;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: #3d3d3d;
`

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const CloseButton = styled(Button)`
    ${baseFont}
    color: #007079;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    text-decoration: none;
    background: none;
    border: none;
    cursor: pointer;

    &:hover {
        background: white;
    }

    @media (max-width: 1024px) {
        color: #007079;
        &:hover {
            background: white;
        }
    }
`

const StyledTextArea = styled(Textarea)`
    gap: 16px;
    text-decoration: none;
    background: none;
    border: none;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    margin-bottom: 0px;
    background: none;

    @media (max-width: 1024px) {
        & > div {
            background: white;
            background-color: white;
        }
    }
`

const RowStyle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
    margin-bottom: 4px;
    gap: 8px;
    background: #ffffff;
    @media (max-width: 1024px) {
        background: none;
    }
    > * {
        margin-right: 0.3rem;
    }
`

const InputStyledLabel = styled(Typography)`
    ${baseFont}
    color: #6f6f6f;
`

const StyledPopoverContent = styled(Typography)`
    max-width: 280px;
    white-space: normal;
    overflow-wrap: anywhere;
`

const RowContainerBottom = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    width: 100%;
    padding: 8px;
    margin-bottom: 0;
    margin-right: 8px;
    gap: 16px;
`

const Label = styled.span`
    font-family: Equinor;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
`

export default TakePhoto
